import React, { useEffect, useState, useCallback, useRef } from 'react'
import styles from './home.module.css'
import DropdownNoImage from '../../Components/DropDown/DropdownNoImage';
import { useGlobalContext } from '../../Context/GlobalContext';
import { formatDateForBackend, generateFullDaySlotsWithOutletStatus, generateTimeSlots } from '../../Utils/utils';
import { getBookingPreventChanges } from '../../Utils/BookingPreventChanges';
import FilterPanel from './Filters/FilterPanel';
import LeftArrow from "../../Assets/images/left-arrow.svg";
import DatePicker from 'react-datepicker';
import RightArrow from "../../Assets/images/right-arrow.svg";
import BackIcon from "../../Assets/images/back.svg";

import ViewIcon from "../../Assets/images/view-only-icon.svg";
import Search from './Search/Search';
import CalendarIcon from '../../Components/Icons/CalendarIcon';
import FilterIcon from '../../Components/Icons/FilterIcon';
import SearchIcon from '../../Components/Icons/SearchIcon';
import DotIcon from '../../Components/Icons/DotIcon';
import StarIcon from '../../Components/Icons/StarIcon';
import RepeatBookingIcon from '../../Components/Icons/RepeatBookingIcon';

const VERSION_FILE_URL = "/version.json";
const DEPLOYMENT_VERSION_STORAGE_KEY = "natureland_deployment_version";
const DEPLOYMENT_BUILD_TIME_STORAGE_KEY = "natureland_deployment_build_time";

const Header = ({ onHardQueueToggle, isHardQueueSidebarOpen, onManualReload, isOutletExtended, onDateContainerChange, isTableLoading = false, isRoomFilterTransitionLoading = false }) => {

const {
    outletOptions,
    setOutletOptions,
    userAssignedOutlets,
    selectedOutlet,
    setSelectedOutlet,
    bookingsList,
    selectedBookingId,
    setSelectedBookingId,
    setIntervalClass,
    tableInterval, setTableInterval,
    selectedDate, setSelectedDate,
    setTimeSlots,
    bookingFilters, setBookingFilters,
    selectedOutletTiming,
    loggedOutletID,
    setOutletAccessMap,
    setIsSidebarVisible,
    setShowBookingSummary,
    setSidebarLoading,
    setFilteredItemIds,
    setFilteredRoomIds,
    publicHoliday,
    isFilterApplied,
    showFilterPanel, setShowFilterPanel,
    isEditBookingActive,
    setDateSyncState,
    newAppointmentSlots,
    newBookingActive,
    setIsBookingSidebar,
    setActiveSlotId,
    setNewAppointmentSlots,
    filterPanelRef,
    isCustomerSelected,
    selectedServices,
    setShowConflictModal,
    setIsNewBookingSlotActive,
    isSidebarVisible,
    editableBooking,
} = useGlobalContext();

const [showSearchPopup, setShowSearchPopup] = useState(false);
const [isBtnDisabled, setIsBtnDisabled] = useState(false);
const [showCalendar, setShowCalendar] = useState(false);
const [isRefreshing, setIsRefreshing] = useState(false);
const [hasDeploymentUpdate, setHasDeploymentUpdate] = useState(false);
// Baseline = version at page load (user already has this build after F5/navigation).
const acknowledgedDeploymentVersionRef = useRef("");
const acknowledgedDeploymentBuildTimeRef = useRef("");
const isInitialDeploymentCheckRef = useRef(true);
const latestDeployedVersionRef = useRef("");
const latestDeployedBuildTimeRef = useRef("");

const syncAcknowledgedDeployment = (version, buildTime) => {
  acknowledgedDeploymentVersionRef.current = version;
  acknowledgedDeploymentBuildTimeRef.current = buildTime;
  if (version) {
    localStorage.setItem(DEPLOYMENT_VERSION_STORAGE_KEY, version);
  }
  if (buildTime) {
    localStorage.setItem(DEPLOYMENT_BUILD_TIME_STORAGE_KEY, buildTime);
  }
};

const isHomeLoaderVisible = isTableLoading || isRoomFilterTransitionLoading;

const runRefresh = useCallback(async (options = {}) => {
    if (!onManualReload) return;
    const { notify = false } = options;
    setIsRefreshing(true);
    // Clear deployment indicator immediately on manual refresh
    setHasDeploymentUpdate(false);
    sessionStorage.removeItem("skipCurrentTimeAutoScroll");
    try {
        await onManualReload();
        if (latestDeployedVersionRef.current || latestDeployedBuildTimeRef.current) {
            syncAcknowledgedDeployment(
                latestDeployedVersionRef.current,
                latestDeployedBuildTimeRef.current
            );
        }
    } finally {
        // Add a small delay for visual feedback if the API call is too fast
        setTimeout(() => {
            setIsRefreshing(false);
            if (notify) {
                try {
                    window.dispatchEvent(new CustomEvent("clearCacheRefreshDone"));
                } catch {}
            }
        }, 500);
    }
}, [onManualReload]);

const handleRefresh = async () => {
    if (isHomeLoaderVisible || isRefreshing) return;
    await runRefresh();
};

useEffect(() => {
    const handleClearCacheRefresh = () => {
        runRefresh({ notify: true });
    };
    window.addEventListener("clearCacheRefresh", handleClearCacheRefresh);
    return () => {
        window.removeEventListener("clearCacheRefresh", handleClearCacheRefresh);
    };
}, [runRefresh]);

useEffect(() => {
    const handleOpenSearch = () => {
        setShowSearchPopup(true);
    };
    const handleToggleFilter = () => {
        if (isTableLoading) return;
        setShowFilterPanel((prev) => !prev);
    };
    window.addEventListener("openHomeSearchPopup", handleOpenSearch);
    window.addEventListener("toggleHomeFilterPanel", handleToggleFilter);
    return () => {
        window.removeEventListener("openHomeSearchPopup", handleOpenSearch);
        window.removeEventListener("toggleHomeFilterPanel", handleToggleFilter);
    };
}, [isTableLoading, setShowFilterPanel]);

useEffect(() => {
    if (isTableLoading) {
        setShowFilterPanel(false);
    }
}, [isTableLoading, setShowFilterPanel]);

useEffect(() => {
    const checkForNewDeployment = async () => {
        try {
            const response = await fetch(
                `${VERSION_FILE_URL}?t=${Date.now()}`,
                { cache: "no-store" }
            );
            if (!response.ok) return;

            const data = await response.json();
            const deployedVersion = data?.version || "";
            const deployedBuildTime = data?.buildTime || "";
            if (!deployedVersion && !deployedBuildTime) return;
            latestDeployedVersionRef.current = deployedVersion;
            latestDeployedBuildTimeRef.current = deployedBuildTime;

            // First check after mount/reload: user already has this build — no dot.
            if (isInitialDeploymentCheckRef.current) {
                isInitialDeploymentCheckRef.current = false;
                syncAcknowledgedDeployment(deployedVersion, deployedBuildTime);
                setHasDeploymentUpdate(false);
                return;
            }

            const versionChanged =
                !!deployedVersion &&
                deployedVersion !== acknowledgedDeploymentVersionRef.current;
            const buildTimeChanged =
                !!deployedBuildTime &&
                deployedBuildTime !== acknowledgedDeploymentBuildTimeRef.current;

            setHasDeploymentUpdate(versionChanged || buildTimeChanged);
        } catch (error) {
            // Ignore deployment check failures and keep normal app flow.
        }
    };

    checkForNewDeployment();
    const intervalId = window.setInterval(checkForNewDeployment, 300000);

    return () => {
        window.clearInterval(intervalId);
    };
}, []);

  useEffect(() => {
    if (userAssignedOutlets?.length) {
      // Deduplicate outlets by ID - if same outlet appears multiple times, prefer edit over view
      const outletMap = new Map();
      userAssignedOutlets.forEach((outlet) => {
        const existing = outletMap.get(outlet.id);
        if (!existing) {
          outletMap.set(outlet.id, outlet);
        } else {
          // If we already have this outlet, prefer edit mode over view mode
          if (outlet.mode === "edit" && existing.mode !== "edit") {
            outletMap.set(outlet.id, outlet);
          }
        }
      });
      
      const uniqueOutlets = Array.from(outletMap.values());
      
      const mapped = uniqueOutlets.map((outlet) => ({
        label: (
          <span className={styles.outletText}>
            {outlet.name}
            {outlet.mode === "view" && (
              <img
                src={ViewIcon}
                alt="View only"
                style={{ width: "14px", height: "14px" }}
              />
            )}
            {outlet.mode === "edit" && "(Edit)"}
          </span>
        ),
        value: outlet.id,
        data: outlet,
      }));

      setOutletOptions(mapped);

      // Default outlet is resolved in GlobalContext when outlet_details updates.

      // Build access map from unique outlets
      const accessMap = {};
      uniqueOutlets.forEach((o) => {
        accessMap[o.id] = o.mode; // "edit" or "view"
      });
      setOutletAccessMap(accessMap);
    }
    // selectedOutlet intentionally omitted: including it causes infinite loop when we call setSelectedOutlet
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAssignedOutlets, loggedOutletID, setOutletAccessMap, setOutletOptions, setSelectedOutlet]);

const handleOutletSelect = (selectedOption) => {

  const hasFilledData = isCustomerSelected || selectedServices.length > 0;
  const newOutletId = selectedOption?.value;
  
  // Don't do anything if selecting the same outlet (already selected/disabled)
  if (newOutletId === selectedOutlet) {
      return false; // Prevent dropdown state update
  }
  
  // If data is filled, show conflict modal and prevent outlet change
  if (hasFilledData) {
      setShowConflictModal(true);
      return false; // Prevent dropdown state update
  }
      
  // No filled data, proceed with outlet change.
  // Clear the booking-redirect skip so the calendar scrolls to the current time.
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("skipCurrentTimeAutoScroll");
  }
  setSelectedOutlet(newOutletId);
  setNewAppointmentSlots([]);
  setIsNewBookingSlotActive(false);
  // Close sidebar when outlet changes
  setIsSidebarVisible(false);
  setIsBookingSidebar(false);
  setShowBookingSummary(false);
  setSelectedBookingId(null);
  setActiveSlotId(null);
  return true; // Allow dropdown state update
};

const getTimeFilterLabel = (duration) => {
    if (duration === 5) return "5 Mins";
    if (duration === 15) return "15 Mins";
    if (duration === 30) return "30 Mins";
    if (duration === 60) return "1 Hr";
    return "15 Mins"; // default
};

const handleTimeFilter = (time) => {
    let duration = 0;
    let intervalClassName = "";

    if (time === "5 Mins") {
      duration = 5;
      intervalClassName = "verySmallInterval";
    } else if (time === "15 Mins") {
      duration = 15;
      intervalClassName = "smallInterval";
    } else if (time === "30 Mins") {
      duration = 30;
      intervalClassName = "mediumInterval";
    } else if (time === "1 Hr") {
      duration = 60;
      intervalClassName = "largeInterval";
    } else return;
    setTableInterval(duration);
    setIntervalClass(intervalClassName);
    const updatedSlots = generateFullDaySlotsWithOutletStatus(
      selectedDate,
      duration,
      selectedOutletTiming,
      isOutletExtended
    );
    setTimeSlots(updatedSlots);
  };




  const config = {
    dayNames: [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ],
    defaultStartTime: "12:00 AM",
    defaultEndTime: "11:59 PM",
  };


  const getTimingForDate = (dateObj, checkOff = true) => {
      const formattedDate = formatDateForBackend(dateObj);
      const timingArray = selectedOutletTiming?.[formattedDate];
  
      if (Array.isArray(timingArray) && timingArray.length > 0) {
        const matchedTiming =
          timingArray.find((entry) => entry.date === formattedDate) ||
          timingArray[0];
  
        if (checkOff && matchedTiming.off === 1) {
          return null;
        }
  
        if (matchedTiming.open && matchedTiming.close) {
          // Check if it's a cross-day shift
          const isCrossDay = matchedTiming.open > matchedTiming.close;
          if (isCrossDay) {
            return {
              start: matchedTiming.open,
              end: "23:59:00",
            };
          } else {
            return {
              start: matchedTiming.open,
              end: matchedTiming.close,
            };
          }
        }
      }
  
      // 🟡 Check yesterday for cross-day timing
      const prevDate = new Date(dateObj);
      prevDate.setDate(prevDate.getDate() - 1);
      const prevDateStr = formatDateForBackend(prevDate);
      const prevTimingArray = selectedOutletTiming?.[prevDateStr];
  
      if (Array.isArray(prevTimingArray) && prevTimingArray.length > 0) {
        const prevTiming = prevTimingArray[0];
  
        if (
          prevTiming &&
          prevTiming.open &&
          prevTiming.close &&
          prevTiming.open > prevTiming.close && // cross-day
          (!checkOff || prevTiming.off !== 1)
        ) {
          return {
            start: "00:00:00",
            end: prevTiming.close,
          };
        }
      }
  
      // Fallback
      return {
        start: config.defaultStartTime,
        end: config.defaultEndTime,
      };
    };


  const generateSlotsForDate = (dateObj) => {
      const timing = getTimingForDate(dateObj);
  
      if (!timing || !timing.start || !timing.end) {
        return [];
      }
  
      return generateTimeSlots(
        timing.start.trim(),
        timing.end.trim(),
        tableInterval
      );
    };
  
  
  const updateDateAndSlots = (newDate) => {
    setSelectedDate(newDate);
    setTimeSlots(generateSlotsForDate(newDate));
  };

  const requestBookingDateSync = useCallback((date, mode) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    if (mode !== "edit" && mode !== "create") return;
    setDateSyncState({
      requestId: Date.now(),
      mode,
      status: "pending",
      date: new Date(date),
    });
  }, [setDateSyncState]);

  const isCreateBookingSidebarOpen = Boolean(newBookingActive && isSidebarVisible);
  // Require editableBooking so stale isEditBookingActive / polluted create mode cannot
  // keep header date locked or defer changes after Update/Cancel.
  const isEditBookingSidebarOpen = Boolean(isEditBookingActive && isSidebarVisible && editableBooking);
  const { preventChanges: isLockedEditBookingDate } = getBookingPreventChanges(editableBooking);
  const isHeaderDateLocked = isEditBookingSidebarOpen && isLockedEditBookingDate;

  const shouldKeepSidebarOnDateChange =
    isEditBookingSidebarOpen || isCreateBookingSidebarOpen || newAppointmentSlots?.length > 0;

  const shouldDeferDateUpdateToSidebar = isEditBookingSidebarOpen || isCreateBookingSidebarOpen;

  const notifyHeaderDateContainerChange = () => {
    if (shouldKeepSidebarOnDateChange) return;
    onDateContainerChange?.();
  };

  const syncHeaderDateWithOpenBooking = (date) => {
    if (isEditBookingSidebarOpen) {
      requestBookingDateSync(date, "edit");
      return;
    }
    if (isCreateBookingSidebarOpen) {
      requestBookingDateSync(date, "create");
    }
  };

  const applyHeaderDateChange = (date) => {
    if (shouldDeferDateUpdateToSidebar) {
      // Let DateTimeOutletSelector.handleDateChange run first so therapist
      // availability sync sees calendarDateSyncSignal + services together.
      syncHeaderDateWithOpenBooking(date);
      return;
    }
    updateDateAndSlots(date);
  };


  const handleCurrentDay = () => {
    if (isHeaderDateLocked) return;
    const currentDate = new Date();
    notifyHeaderDateContainerChange();
    applyHeaderDateChange(currentDate);
  };

  const isSelectedDateToday = selectedDate && (() => {
    const today = new Date();
    return selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();
  })();


  const handlePrevDay = () => {
    if (isBtnDisabled || isHeaderDateLocked) return;
    setIsBtnDisabled(true);
    notifyHeaderDateContainerChange();
    const newDate = new Date(selectedDate || new Date());
    newDate.setDate(newDate.getDate() - 1);
    applyHeaderDateChange(newDate);
    if (shouldKeepSidebarOnDateChange){
    }else{
      setIsBookingSidebar(false);
      setIsSidebarVisible(false);
      setSelectedBookingId(null);
      setActiveSlotId(null);
      setSelectedBookingId(null);
      setNewAppointmentSlots([]);
    }
    setTimeout(() => setIsBtnDisabled(false), 1000);
  };

  const handleNextDay = () => {
    if (isBtnDisabled || isHeaderDateLocked) return;
    setIsBtnDisabled(true);
    notifyHeaderDateContainerChange();
    const newDate = new Date(selectedDate || new Date());
    newDate.setDate(newDate.getDate() + 1);
    applyHeaderDateChange(newDate);
    if (shouldKeepSidebarOnDateChange){
    }else{
      setIsBookingSidebar(false);
      setIsSidebarVisible(false);
      setSelectedBookingId(null);
      setActiveSlotId(null);
      setSelectedBookingId(null);
      setNewAppointmentSlots([]);
    }
    setTimeout(() => setIsBtnDisabled(false), 1000);
  };

  const handleDateChange = (date) => {
      if (isHeaderDateLocked) return;
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
      const isSameSelectedDay = selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime()) && date.getFullYear() === selectedDate.getFullYear() &&date.getMonth() === selectedDate.getMonth() &&date.getDate() === selectedDate.getDate();
      if (isSameSelectedDay) {setShowCalendar(false); return; }
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("skipCurrentTimeAutoScroll");
      }
      notifyHeaderDateContainerChange();
      setShowCalendar(false);
      if (shouldDeferDateUpdateToSidebar) {
        applyHeaderDateChange(date);
      } else {
        setSelectedDate(date);
        const startTime = config.defaultStartTime;
        const endTime = config.defaultEndTime;
        const newTimeSlots = generateTimeSlots(startTime, endTime, tableInterval);
        setTimeSlots(newTimeSlots);
      }
      if (shouldKeepSidebarOnDateChange){
      }else{
        setIsBookingSidebar(false);
        setIsSidebarVisible(false);
        setSelectedBookingId(null);
        setActiveSlotId(null);
        setSelectedBookingId(null);
        setNewAppointmentSlots([]);
      }
    };
  
  const onBookingSelect = ({booking_id,booking_date}) => {
    const bookingDate = new Date(booking_date);
    const isValidBookingDate = !Number.isNaN(bookingDate.getTime());
    const isSameBooking = String(selectedBookingId ?? "") === String(booking_id ?? "");
    const isSameSelectedDay =
      isValidBookingDate &&
      selectedDate instanceof Date &&
      !Number.isNaN(selectedDate.getTime()) &&
      bookingDate.getFullYear() === selectedDate.getFullYear() &&
      bookingDate.getMonth() === selectedDate.getMonth() &&
      bookingDate.getDate() === selectedDate.getDate();

    if (isValidBookingDate && !isSameSelectedDay) {
      setSelectedDate(bookingDate);
    }
    setShowSearchPopup(false);
		setIsSidebarVisible(true);
    setShowBookingSummary(true);
    setSelectedBookingId(booking_id);
    if (!isSameBooking || (isValidBookingDate && !isSameSelectedDay)) {
      setSidebarLoading(true);
    }
    sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("scrollToBooking", {
          detail: {
            booking_id,
            source: "search",
          },
        })
      );
    }, 100);
	}

  const closePopup = () => {
		setShowSearchPopup(false)
	}

  const ReadOnlyInput = React.forwardRef(({ value, onClick, disabled }, ref) => (
  <input
    ref={ref}
    value={value}
    onClick={onClick}
    readOnly
    disabled={disabled}
    className={`${styles.formControl} ${styles.readOnly} form-control ${disabled ? "disabled" : ""}`}
  />
));


  return (
    <div className={styles.headerMenu}>
        <div className={styles.leftSection}>
          {isHardQueueSidebarOpen ? (
            <button 
              className={styles.hamburgerButton} 
              aria-label="Back"
              onClick={onHardQueueToggle}
            >
              <img src={BackIcon} alt="Back" />
            </button>
          ) : (
            <button 
              className={styles.hamburgerButton} 
              aria-label="Menu"
              onClick={onHardQueueToggle}
            >
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
            </button>
          )}
          {/* Refresh Button Starts */}
          <div className={`${styles.refreshButtonContainer} ${isHomeLoaderVisible || isRefreshing ? styles.disabledBtnContainer : ''}`}>
          <button
              type="button"
              onClick={handleRefresh}
              disabled={isHomeLoaderVisible || isRefreshing}
              className={`${styles.refreshButton} ${isRefreshing ? styles.isRefreshing : ''} ${isHomeLoaderVisible || isRefreshing ? styles.disabledBtn : ''}`}
              title={
                isHomeLoaderVisible
                  ? "Please wait until loading finishes"
                  : hasDeploymentUpdate
                  ? "New version available — refresh to update"
                  : "Refresh data"
              }
          >
              <RepeatBookingIcon size={16} />
              {hasDeploymentUpdate && !isRefreshing && !isHomeLoaderVisible && (
                <span className={styles.updateIndicator}></span>
              )}
          </button>
          </div>
          {/* Refresh Button Ends */}
          <div className={styles.columnDropdown}>
              <DropdownNoImage options={outletOptions}
                selectedLabel={
                  outletOptions.find(
                  (outlet) => outlet.value === selectedOutlet
                  )?.label || ""
                }
                selectedValue={selectedOutlet}
                onSelect={handleOutletSelect}
                // onOpen={fetchOutletsList}
                dropdownClass={`${styles.outletFilter} headerOutletFilter`}
                dropdownMenuClass={styles.outletFilterList}
                dropdownOptionsClass="outlet-option"
                placeholder="Outlet"
                placeholderClass="greyPlaceholder"
                labelText=""
              />
              <DropdownNoImage
                options={["5 Mins", "15 Mins", "30 Mins", "1 Hr"]}
                defaultOption="15 Mins"
                selectedLabel={getTimeFilterLabel(tableInterval)}
                selectedValue={getTimeFilterLabel(tableInterval)}
                onSelect={(time) => handleTimeFilter(time, setTableInterval)}
                dropdownClass={`${styles.timeFilter} headerTimeSpamFilter`}
                dropdownMenuClass={`${styles.timeFilterList}`}
              />
          </div>
        </div>
            <div className={styles.rightFilters}>
                <div
                  onClick={() => setShowSearchPopup(true)}
                  className={`${styles.searchLink} ${styles.commonBorderWText} d-flex align-center`}
                >
                  <SearchIcon size={20}/>
                  <p>Search bookings by phone/name</p>
                </div>
                {showSearchPopup && (
                <Search
                    bookings={bookingsList}
                    onBookingSelect={onBookingSelect}
                    closePopup={closePopup}
                />
                )}


                {/* Filter Section Starts */}
                <div className={`position-relative`}>
                  <div
                    className={`${styles.FilterBtn} ${styles.commonBorderWText} d-flex home-filter prevent-outside-click hover ${isTableLoading ? styles.disabledBtn : ''}`}
                    aria-disabled={isTableLoading}
                    onClick={(e) => {
                      e.stopPropagation(); // stops the outside click listener
                      if (isTableLoading) return;
                      setShowFilterPanel((prev) => !prev); // toggle on/off
                    }}
                  >
                    <p className={styles.filterText}>Filter</p>
                    <FilterIcon size={20} />
                    {isFilterApplied === true && !isTableLoading && (
                      <DotIcon size={10} color="#FF0000" className={styles.filterIndicator} />
                    )}
                  </div>

                      
                  {showFilterPanel && (
                      <FilterPanel
                      ref={filterPanelRef}
                      filters={bookingFilters}
                      onFilterChange={setBookingFilters}
                      onClose={() => setShowFilterPanel(false)}
                      onItemIdsChange={setFilteredItemIds}
                      onRoomIdsChange={setFilteredRoomIds}
                      />
                  )}
                </div>

                {/* Filter Section Ends */}

                {/* Date Section Starts */}
                <div className={styles.dateContainer}>
                  {publicHoliday && (
                    <div className={styles.publicHoliday}><StarIcon size={20} fill="#ff000" starFill="#fff" />PH</div>
                  )}
                  <button className={`${styles.currentBtn} prevent-outside-click`}
                    onClick={handleCurrentDay}
                    disabled={isSelectedDateToday || isHeaderDateLocked}
                  >
                    Today
                  </button>
                  <button
                      className={`${styles.btn} prevent-outside-click`}
                      onClick={handlePrevDay}
                      disabled={isBtnDisabled || isHeaderDateLocked}
                  >
                      <img src={LeftArrow} alt="left-arrow" />
                  </button>
                  <div className={`${styles.formControlWrapper} prevent-outside-click`} onClick={(e) => {
                    if (isHeaderDateLocked) return;
                    // Only toggle calendar if click is not inside the calendar dropdown
                    if (!e.target.closest('.react-datepicker')) {
                      setShowCalendar(!showCalendar);
                    }
                    }}
                  >
                    <DatePicker
                      locale="en"
                      selected={selectedDate}
                      onChange={handleDateChange}
                      dateFormat="EEE, MMM d" 
                      open={showCalendar && !isHeaderDateLocked}
                      onClickOutside={() => setShowCalendar(false)}
                      className={`${styles.formControl} ${styles.readOnly} form-control `}
                      calendarClassName={styles.customDatePicker}
                      customInput={<ReadOnlyInput disabled={isHeaderDateLocked} />}
                      disabled={isHeaderDateLocked}
                    />
                  </div>
                  <button
                      className={`${styles.btn} prevent-outside-click`}
                      onClick={handleNextDay}
                      disabled={isBtnDisabled || isHeaderDateLocked}
                  >
                    <img src={RightArrow} alt="right-arrow" />
                  </button>
                  <button
                    className={`${styles.btn} ${styles.calendarShown} prevent-outside-click`}
                    onClick={() => {
                      if (isHeaderDateLocked) return;
                      setShowCalendar(!showCalendar);
                    }}
                    disabled={isHeaderDateLocked}
                  >
                    <CalendarIcon size={20} strokeColor="black" />
                  </button>
                </div>
                {/* Date Section Ends */}
            </div>
            </div>
  )
}

export default Header